// Card 64, the hold site (R964, ui-map-interface.md §17.3.64).
//
// The capital that was. Map generation draws up to three winners of
// EARLIER seasons out of the world's winner list and keeps one water cell
// free for each of them (`numbers.md` §14.1). The chart draws a mark on
// that cell and writes whose hold stood there.
//
// **A HOLD SITE IS NOT A SITE.** Nothing of the sites layer draws it: the
// wire carries no `Site`, the cell takes no hold and no installation, and
// `commands.clickMap` finds nothing there. It is a mark on open water, and
// a fleet sails over it.
//
// **THE HISTORICAL SYMBOL IS THE PLAYER'S.** A hold wears one of the six
// hold symbols (R633), and a ruin wears the broken form of the one the
// player picked. The historical reading asks the atlas for the SAME cell
// the sites layer asks for, so a reader who changed the symbol sees one
// convention on the map and not two. The ruin colour is baked into the
// cell, so the piece adds no tint.
//
// **THE MODERN MARK IS A STAR** (R1248). The owner picked card 10 of
// mockup round 059 for the modern reading: a filled five-point star in the
// pale ink, for EVERY hold symbol. The first line keeps the words "HERE
// STOOD THE HOLD OF", so no strike marks the fallen cartouche any more.
//
// **THE OWNER ASKED FOR IT LIGHT.** The card drew an anchor beside the
// ruin; the pick dropped it. The piece is one sprite and two lines.
//
// **THE SYMBOL CHANGE IS AN EDGE** (R801). No rebuild reason names the
// hold-symbol setting, so the historical reading answers `outgrown` with
// one compare a frame: the symbol it packed against the symbol the scene
// holds now. The modern star does not read the symbol, so a change there
// repacks nothing.

import { chart as S } from '../../../gen/strings';
import { MonumentKind, type Monument } from '../../../gen/proto/aquaphobia';
import { cellCenter } from '../../../world';
import { chartMarkKey, siteHoldRuinKey } from '../../gfx/atlas';
import { DEFAULT_HOLD_SYMBOL, type HoldSymbolId } from '../../../render/hold-marks';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { champion } from './memorial-reef';
import { CHART_INK, faceOf, inkOf } from './shared';

/** One hold site, as the last pack read it. */
interface Site {
  x: number;
  y: number;
  /** The winner's name as the season closed, and the season she won. */
  commander: string;
  season: number;
}

export class HoldSitePiece implements Piece {
  readonly id = 'hold_site' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The hold fell before this season opened, so a replay film keeps it. */
  readonly film = 'keep' as const;
  readonly rebuildOn = ['hello', 'reading', 'zoomband', 'font'] as const;

  /** The hold sites of the last pack. The tests read them. */
  sites: Site[] = [];

  /**
   * The hold symbol the last pack drew, for `outgrown`. A scene that names
   * none takes the stored default.
   */
  private symbol: HoldSymbolId = DEFAULT_HOLD_SYMBOL;

  visible(ctx: ChartContext): boolean {
    return ctx.state.monuments.some(isHoldSite);
  }

  rebuild(ctx: ChartContext): void {
    this.symbol = ctx.scene.holdSymbol ?? DEFAULT_HOLD_SYMBOL;
    this.sites = [];
    for (const m of ctx.state.monuments) {
      if (!isHoldSite(m) || m.cells.length === 0) continue;
      const [x, y] = cellCenter(m.cells[0]);
      this.sites.push({ x, y, commander: m.commander, season: m.season });
    }
  }

  outgrown(ctx: ChartContext): boolean {
    return ctx.reading === 'historical'
      && (ctx.scene.holdSymbol ?? DEFAULT_HOLD_SYMBOL) !== this.symbol;
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const t = ctx.cfg.pieces.hold_site;
    const r = t[ctx.reading];
    const hist = ctx.reading === 'historical';
    const face = faceOf(ctx.reading);
    const ink = inkOf(r.ink, CHART_INK.pale);
    const mark = ctx.glyphs.mark(hist ? siteHoldRuinKey(this.symbol, 'h') : chartMarkKey('star'));
    for (const s of this.sites) {
      if (hist) {
        // The ruin cell carries its own colour, so the sprite takes no tint
        // of its own. `ink = 0` in the pack therefore reads as white here.
        sinks.quads.sprite(
          s.x, s.y, 0, mark, inkOf(r.ink, 0xffffff), t.fig.alpha * r.alpha,
          t.fig.symbol_scale,
        );
      } else {
        // The star's centre is the cell centre and not its box centre.
        sinks.quads.sprite(
          s.x, s.y, 0, mark, ink, t.fig.star_alpha * r.alpha, 1, 0, false,
        );
      }
      const head = s.y + t.fig.label_dy;
      // The two lines are one block, so they grow about one pivot.
      const pivot = { x: s.x, y: head };
      ctx.glyphs.word(
        sinks.quads,
        hist ? S.hold_site_historical({ name: s.commander })
          : S.hold_site_modern({ name: s.commander }),
        s.x, head, face, t.text.main, ink, r.alpha, { anchor: 'centre', pivot },
      );
      ctx.glyphs.word(
        sinks.quads, champion(ctx, s.season, hist),
        s.x, head + t.fig.line_h,
        face, t.text.sub, CHART_INK.dim, r.alpha, { anchor: 'centre', pivot },
      );
    }
  }
}

/** Is this monument a hold site? A pool monument is piece 65 (R1248). */
function isHoldSite(m: Monument): boolean {
  return m.kind === MonumentKind.MONUMENT_KIND_HOLD_SITE;
}

/** The one instance the registry holds. */
export const holdSite = new HoldSitePiece();
